/**
 * Bemenet: billentyűzet, egér, egérzár.
 *
 * A nyers eseményeket felhalmozza, és a szimulációs tick olvassa ki egyszerre.
 * Ez fontos: egy 144 Hz-es egér több eseményt küld tickenként, és ha csak az
 * utolsót néznénk, elveszne a mozgás egy része.
 *
 * ---------------------------------------------------------------------------
 * AZ EGÉRZÁR MODELLJE — szándékosan a lehető legegyszerűbb
 * ---------------------------------------------------------------------------
 *
 *   nincs ──(felhasználói kattintás)──▶ függőben ──▶ zárva
 *     ▲                                     │           │
 *     └──────────────(elutasítás)───────────┘           │
 *     └──────────────────(elvesztés)────────────────────┘
 *
 * Négy szabály, semmi több:
 *
 *  1. Zárat KIZÁRÓLAG valódi felhasználói kattintás kér. Időzítőből,
 *     automatikus újrapróbából soha — a böngésző friss gesztust követel,
 *     tehát az elvi okból sem működhet.
 *  2. Egyszerre egy kérés lehet függőben. A `locked` önmagában nem elég
 *     őrszem: amíg a kérés folyamatban van, a zár még nincs meg, így egy
 *     második kérés is átmenne.
 *  3. Késve érkező hibát eldobunk, ha a zár közben megvan. A hiba az
 *     ígéret elutasításaként ÉS `pointerlockerror` eseményként is jöhet —
 *     ugyanannak a kérésnek a két arca, egyszer kezeljük. Enélkül egy
 *     korábbi kérés hibája leszedi a közben megszerzett zárat.
 *  4. Ha nincs zár, nincs egérrel nézés. A játék megáll, és egy
 *     kattintással folytatható; közben billentyűvel is lehet fordulni.
 *
 * Ami korábban itt volt, és törölve lett, mert több kárt okozott, mint hasznot:
 *  - „tartalék" nézés a kurzor képernyő-koordinátáiból. Zár alatt a kurzor
 *    nem mozog, tehát a tárolt koordináta elavul, és az első esemény után
 *    több száz képpontnyi ugrást adott — ettől pördült oldalra a karakter.
 *  - peremzónás automatikus fordulás,
 *  - automatikus újrapróba időzítővel (lásd az 1. szabályt),
 *  - `unadjustedMovement: true`. A Chromiumban Windows/ChromeOS-funkció;
 *    X11 alatt minden kérésre `NotSupportedError`, vagyis ott garantáltan
 *    elbukott az első próbálkozás. Platformfüggő kérés helyett inkább
 *    semmilyen kérés: így minden rendszeren ugyanaz az egy útvonal fut.
 *
 * Gazda: X
 */

import type { InputState } from '../core/contracts'
import { DEG } from '../core/math'

export interface InputOptions {
  /** Fok / képpont. A GDD szerint 0.10 az induló érték. */
  sensitivity: number
  invertY: boolean
}

const DEFAULT_BINDINGS = {
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA'],
  right: ['KeyD'],
  /** Billentyűs fordulás — egérzár nélkül is játszható marad a játék. */
  turnLeft: ['ArrowLeft', 'KeyQ'],
  turnRight: ['ArrowRight', 'KeyE'],
  jump: ['Space'],
  use: ['KeyF'],
  run: ['ShiftLeft', 'ShiftRight'],
}

/** Billentyűs fordulási sebesség (radián/másodperc). */
const KEY_TURN_SPEED = 2.4
const TICK = 1 / 60

/**
 * Egy szimulációs tick alatt ennyinél többet nem fordulhat a nézés.
 * Jogos egérmozgás 16 ezredmásodperc alatt sosem ad ennyit, tehát ez egy
 * ártalmatlan felső korlát, ami minden hibás bemeneti csúcsot elnyel.
 */
const MAX_LOOK_DELTA_PER_TICK = 0.6

export class Input {
  readonly state: InputState = {
    moveX: 0,
    moveZ: 0,
    lookYaw: 0,
    lookPitch: 0,
    fire: false,
    firePressed: false,
    usePressed: false,
    run: false,
    weaponSlot: null,
    weaponCycle: 0,
    active: false,
  }

  options: InputOptions = { sensitivity: 0.1, invertY: false }

  private down = new Set<string>()
  private mouseDown = false
  private pendingYaw = 0
  private pendingPitch = 0
  private pendingFire = false
  private pendingUse = false
  private pendingSlot: number | null = null
  private pendingCycle = 0
  private pendingJump = false
  private jumpHeld = false

  /** Van-e épp függőben lévő zárkérés (a 2. szabály őrszeme). */
  private lockPending = false
  /** Egymást követő elutasítások — csak a figyelmeztetéshez számoljuk. */
  private lockRejects = 0

  /** Külső feliratkozók. */
  onEscape: (() => void) | null = null
  onConfirm: (() => void) | null = null
  onToggleDebug: (() => void) | null = null
  onCopyDiagnostics: (() => void) | null = null
  /** Minden elutasított zárkérés — a diagnosztikai naplóhoz. */
  onLockRejected: (() => void) | null = null
  /** Ismételten sikertelen zárkérés: szóljunk a játékosnak. */
  onLockFailed: (() => void) | null = null

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.handleKeyDown)
    window.addEventListener('keyup', this.handleKeyUp)
    window.addEventListener('mousedown', this.handleMouseDown)
    window.addEventListener('mouseup', this.handleMouseUp)
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('wheel', this.handleWheel, { passive: true })
    document.addEventListener('pointerlockchange', this.handleLockChange)
    document.addEventListener('pointerlockerror', this.handleLockError)
    window.addEventListener('blur', this.handleBlur)
  }

  // -------------------------------------------------------------------------
  // Egérzár
  // -------------------------------------------------------------------------

  /** Csak valódi felhasználói gesztusból hívandó (1. szabály). */
  requestLock(): void {
    if (this.locked || this.lockPending) return
    this.lockPending = true

    try {
      const res = this.canvas.requestPointerLock() as unknown

      // Régebbi böngészőkben `void`, újabbakban `Promise`.
      if (res && typeof (res as Promise<void>).then === 'function') {
        void (res as Promise<void>)
          .then(() => {
            this.lockPending = false
          })
          .catch(() => {
            this.lockPending = false
            if (this.locked) return // 3. szabály
            this.handleLockRejected()
          })
      } else {
        this.lockPending = false
      }
    } catch {
      this.lockPending = false
      if (!this.locked) this.handleLockRejected()
    }
  }

  releaseLock(): void {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock()
  }

  get locked(): boolean {
    return document.pointerLockElement === this.canvas
  }

  private handleLockRejected(): void {
    this.lockRejects++
    this.onLockRejected?.()
    // Egy-két elutasítás normális: a böngésző a zár elvesztése után rövid
    // ideig friss gesztust vár. Csak tartós kudarcnál szólunk a játékosnak.
    if (this.lockRejects >= 3) this.onLockFailed?.()
  }

  private handleLockChange = (): void => {
    this.lockPending = false
    this.state.active = this.locked
    if (this.locked) {
      this.lockRejects = 0
    } else {
      // Zár nélkül minden gomb elenged — különben „beragadna" a mozgás.
      this.down.clear()
      this.mouseDown = false
    }
  }

  private handleLockError = (): void => {
    this.lockPending = false
    if (this.locked) return // 3. szabály
    this.handleLockRejected()
  }

  // -------------------------------------------------------------------------
  // Egér és billentyűzet
  // -------------------------------------------------------------------------

  private handleBlur = (): void => {
    this.down.clear()
    this.mouseDown = false
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') {
      this.onEscape?.()
      return
    }
    // Az F3-at sok böngésző és laptop-billentyűzet elfogja, ezért a
    // backtick az egyenrangú alternatívája.
    if (e.code === 'F3' || e.code === 'Backquote') {
      e.preventDefault()
      this.onToggleDebug?.()
      return
    }
    if (e.code === 'KeyC' && !e.ctrlKey && !e.metaKey) {
      this.onCopyDiagnostics?.()
    }
    if (e.code === 'Enter') {
      this.onConfirm?.()
      return
    }
    if (e.code === 'Space' || e.code.startsWith('Arrow') || e.code === 'Tab') e.preventDefault()

    if (this.down.has(e.code)) return
    this.down.add(e.code)

    if (DEFAULT_BINDINGS.use.includes(e.code)) this.pendingUse = true
    if (DEFAULT_BINDINGS.jump.includes(e.code) && !this.jumpHeld) {
      this.pendingJump = true
      this.jumpHeld = true
    }

    if (e.code.startsWith('Digit')) {
      const n = Number(e.code.slice(5))
      if (n >= 1 && n <= 9) this.pendingSlot = n
    }
  }

  private handleKeyUp = (e: KeyboardEvent): void => {
    this.down.delete(e.code)
    if (DEFAULT_BINDINGS.jump.includes(e.code)) this.jumpHeld = false
  }

  private handleMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) {
      this.mouseDown = true
      this.pendingFire = true
    } else if (e.button === 2) {
      this.pendingUse = true
    }
  }

  private handleMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseDown = false
  }

  /** Körbenézés kizárólag egérzár alatt, a nyers elmozdulásból (4. szabály). */
  private handleMouseMove = (e: MouseEvent): void => {
    if (!this.locked) return
    this.pendingYaw -= e.movementX * this.options.sensitivity * DEG
    this.pendingPitch -=
      e.movementY * this.options.sensitivity * DEG * (this.options.invertY ? -1 : 1)
  }

  private handleWheel = (e: WheelEvent): void => {
    if (!this.locked) return
    this.pendingCycle += Math.sign(e.deltaY)
  }

  private has(list: string[]): boolean {
    for (const code of list) if (this.down.has(code)) return true
    return false
  }

  // -------------------------------------------------------------------------
  // Tick
  // -------------------------------------------------------------------------

  /** A tick elején hívandó: összeállítja az állapotot. */
  beginTick(): void {
    const s = this.state
    const b = DEFAULT_BINDINGS

    let mx = 0
    let mz = 0
    if (this.has(b.right)) mx += 1
    if (this.has(b.left)) mx -= 1
    if (this.has(b.forward)) mz += 1
    if (this.has(b.back)) mz -= 1

    // Normalizálás: átlósan sincs sebességbónusz (GDD 6. pont).
    const len = Math.hypot(mx, mz)
    if (len > 1) {
      mx /= len
      mz /= len
    }
    s.moveX = mx
    s.moveZ = mz

    let turn = 0
    if (this.has(b.turnLeft)) turn += 1
    if (this.has(b.turnRight)) turn -= 1

    let lookYaw = this.pendingYaw + turn * KEY_TURN_SPEED * TICK
    let lookPitch = this.pendingPitch

    // Felső korlát a két tengely EGYÜTTES nagyságára, hogy az átlós nézés
    // se kerülhesse meg a védelmet.
    const lookLength = Math.hypot(lookYaw, lookPitch)
    if (lookLength > MAX_LOOK_DELTA_PER_TICK) {
      const scale = MAX_LOOK_DELTA_PER_TICK / lookLength
      lookYaw *= scale
      lookPitch *= scale
    }
    s.lookYaw = lookYaw
    s.lookPitch = lookPitch
    this.pendingYaw = 0
    this.pendingPitch = 0

    s.fire = this.mouseDown
    s.firePressed = this.pendingFire
    this.pendingFire = false

    s.usePressed = this.pendingUse
    this.pendingUse = false

    s.run = this.has(b.run)
    s.weaponSlot = this.pendingSlot
    this.pendingSlot = null
    s.weaponCycle = this.pendingCycle
    this.pendingCycle = 0
  }

  /** Ugrás lekérdezése (egy tickre él). */
  consumeJump(): boolean {
    const j = this.pendingJump
    this.pendingJump = false
    return j
  }

  dispose(): void {
    window.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('keyup', this.handleKeyUp)
    window.removeEventListener('mousedown', this.handleMouseDown)
    window.removeEventListener('mouseup', this.handleMouseUp)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('wheel', this.handleWheel)
    document.removeEventListener('pointerlockchange', this.handleLockChange)
    document.removeEventListener('pointerlockerror', this.handleLockError)
    window.removeEventListener('blur', this.handleBlur)
  }
}
